import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog'
import { Button } from '../ui/Button'
import type {
  HabitAdjustmentCheckin,
  HabitAdjustmentChooseActionResponse,
  HabitAdjustmentConfirmResponse,
  HabitAdjustmentDecisionResponse,
  HabitAdjustmentRespondResponse,
  HabitAdjustmentSimpleResponse,
} from '../../services/habitAdjustmentService'

const REASONS = [
  ['timing', 'Timing is off'],
  ['energy', 'Energy is low'],
  ['interest', 'This version feels stale'],
  ['external_events', 'Life is in the way'],
  ['goal_fit', 'This may not fit anymore'],
  ['something_else', 'Something else'],
] as const

const WEEKDAYS = [
  ['MO', 'Mon'],
  ['TU', 'Tue'],
  ['WE', 'Wed'],
  ['TH', 'Thu'],
  ['FR', 'Fri'],
  ['SA', 'Sat'],
  ['SU', 'Sun'],
] as const

const TIME_WINDOWS = [
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
  ['evening', 'Evening'],
  ['night', 'Night'],
] as const

type Step = 'diagnose' | 'options' | 'confirm' | 'pause'

type ChosenDetails = Record<string, unknown>

function isoDateOnly(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function buildPauseUntilIso(daysFromNow: number): string {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return d.toISOString()
}

function formatLongDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function summarize(actionId: string, details: ChosenDetails, habitTitle: string, pauseUntil?: string): string {
  switch (actionId) {
    case 'keep_as_is':
      return `We won’t change anything. You can come back to this whenever you like.`
    case 'make_lighter': {
      const dur = Number(details.default_duration_minutes ?? 10)
      return `We’ll shrink ${habitTitle} to about ${dur} minutes per session.`
    }
    case 'move_time': {
      const win = String(details.preferred_time_window ?? '')
      const start = String(details.preferred_start_time ?? '')
      if (win || start) {
        return `We’ll move ${habitTitle} to ${win || 'a new slot'}${start ? ` (around ${start})` : ''}.`
      }
      return `We’ll move ${habitTitle} to a slot that fits better.`
    }
    case 'move_window':
      return `We’ll try a different time window for ${habitTitle}.`
    case 'reduce_frequency': {
      const days = (details.preferred_days as string[] | undefined) || []
      const perWeek = Number(details.days_per_week ?? days.length ?? 2)
      const dayLabel = days.length ? ` (${days.join(', ')})` : ''
      return `We’ll reduce ${habitTitle} to ${perWeek} day${perWeek === 1 ? '' : 's'} per week${dayLabel}.`
    }
    case 'swap_activity':
      return `We’ll switch ${habitTitle} to a nearby version. You can edit the description after.`
    case 'pause_7_days':
      return `We’ll pause ${habitTitle} for 1 week. We’ll bring it back gently when the time is up.`
    case 'pause_14_days':
      return `We’ll pause ${habitTitle} for 2 weeks. We’ll bring it back gently when the time is up.`
    case 'archive':
      return `We’ll archive ${habitTitle}. Your history is preserved and you can restore it anytime.`
    default:
      if (actionId.startsWith('pause_') && pauseUntil) {
        return `We’ll pause ${habitTitle} until ${formatLongDate(pauseUntil)}.`
      }
      return `This change only applies after you confirm it.`
  }
}

const PAUSE_PRESETS: Array<{ id: string; label: string; days: number | null }> = [
  { id: 'next_week', label: 'Next week', days: 7 },
  { id: 'two_weeks', label: '2 weeks', days: 14 },
  { id: 'next_month', label: 'Next month', days: 30 },
  { id: 'custom', label: 'Pick a date', days: null },
]

export function HabitAdjustmentDialog({
  open,
  onOpenChange,
  checkin,
  initialStep = 'diagnose',
  respond,
  diagnose,
  chooseAction,
  confirm,
  onPause,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  checkin: HabitAdjustmentCheckin
  initialStep?: Step
  respond: (
    checkinId: string,
    response: 'adjust' | 'pause' | 'keep_as_is' | 'not_now',
  ) => Promise<HabitAdjustmentRespondResponse>
  diagnose: (checkinId: string, reasons: string[]) => Promise<HabitAdjustmentDecisionResponse>
  chooseAction: (
    decisionId: string,
    actionId: string,
    details?: Record<string, unknown>,
  ) => Promise<HabitAdjustmentChooseActionResponse>
  confirm: (decisionId: string) => Promise<HabitAdjustmentConfirmResponse>
  onPause: (
    habitDefId: number,
    payload: { paused_until?: string; reason_codes?: string[]; source?: string },
  ) => Promise<HabitAdjustmentSimpleResponse>
  onDone: () => void
}) {
  const [step, setStep] = useState<Step>(initialStep)
  const [selectedReasons, setSelectedReasons] = useState<string[]>([])
  const [decisionId, setDecisionId] = useState<string | null>(null)
  const [options, setOptions] = useState<Array<{ action_id: string; label: string }>>([])
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [chosenDetails, setChosenDetails] = useState<ChosenDetails>({})
  const [copy, setCopy] = useState<{ title?: string; body?: string }>({})
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [preferredDays, setPreferredDays] = useState<string[]>([])
  const [lighterMinutes, setLighterMinutes] = useState<number>(10)
  const [preferredTimeWindow, setPreferredTimeWindow] = useState<string>('morning')
  const [preferredStartTime, setPreferredStartTime] = useState<string>('09:00')
  const [swapDescription, setSwapDescription] = useState<string>('')
  const [pauseChoice, setPauseChoice] = useState<string>('two_weeks')
  const [customDate, setCustomDate] = useState<string>(isoDateOnly(new Date(Date.now() + 14 * 86400000)))

  useEffect(() => {
    if (open) {
      setStep(initialStep)
      setError(null)
    }
  }, [open, initialStep])

  const habitTitle = checkin.habit_title || 'this habit'

  const pauseUntilIso = useMemo(() => {
    const preset = PAUSE_PRESETS.find(p => p.id === pauseChoice)
    if (preset && preset.days) return buildPauseUntilIso(preset.days)
    if (pauseChoice === 'custom') {
      try {
        const d = new Date(customDate)
        if (Number.isNaN(d.getTime())) return buildPauseUntilIso(14)
        return d.toISOString()
      } catch {
        return buildPauseUntilIso(14)
      }
    }
    return buildPauseUntilIso(14)
  }, [pauseChoice, customDate])

  const toggleReason = (reason: string) => {
    setSelectedReasons(prev =>
      prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
    )
  }

  const toggleDay = (day: string) => {
    setPreferredDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]))
  }

  const handleDiagnose = async () => {
    setWorking(true)
    setError(null)
    try {
      const result = await diagnose(checkin.id, selectedReasons)
      setDecisionId(result.decision?.id || null)
      const incoming = result.recommendation?.options || []
      setOptions(incoming)
      setCopy({ title: result.recommendation?.title, body: result.recommendation?.body })
      setSelectedAction(incoming[0]?.action_id || null)
      setStep('options')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load options. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const handleEscalateToPause = () => {
    setStep('pause')
  }

  const detailsForReview = (): ChosenDetails => {
    if (selectedAction === 'make_lighter') {
      return {
        default_duration_minutes: Math.max(1, Math.min(240, Number(lighterMinutes) || 10)),
      }
    }
    if (selectedAction === 'move_time' || selectedAction === 'move_window') {
      return {
        preferred_time_window: preferredTimeWindow,
        preferred_start_time: preferredStartTime,
      }
    }
    if (selectedAction === 'reduce_frequency') {
      return {
        structured_recurrence: {
          frequency_type: 'weekly',
          days_per_week: preferredDays.length || 2,
          preferred_days: preferredDays.length ? preferredDays : undefined,
          interval_value: 1,
        },
        days_per_week: preferredDays.length || 2,
        preferred_days: preferredDays.length ? preferredDays : undefined,
      }
    }
    if (selectedAction === 'swap_activity') {
      return {
        description: swapDescription.trim(),
      }
    }
    return {}
  }

  const canReviewSelectedAction = () => {
    if (!selectedAction || working) return false
    if (selectedAction === 'swap_activity') return swapDescription.trim().length > 0
    if (selectedAction === 'make_lighter') return Number(lighterMinutes) > 0
    if (selectedAction === 'move_time' || selectedAction === 'move_window') {
      return Boolean(preferredTimeWindow && preferredStartTime)
    }
    return true
  }

  const handleReview = async () => {
    if (!decisionId || !selectedAction) return
    setWorking(true)
    setError(null)
    try {
      const details = detailsForReview()
      await chooseAction(decisionId, selectedAction, details)
      setChosenDetails(details)
      setStep('confirm')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not lock in this option. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const handleConfirm = async () => {
    if (!decisionId) return
    setWorking(true)
    setError(null)
    try {
      await confirm(decisionId)
      onDone()
      onOpenChange(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not confirm the change. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const handleKeepAsIs = async () => {
    setWorking(true)
    setError(null)
    try {
      await respond(checkin.id, 'keep_as_is')
      onDone()
      onOpenChange(false)
    } finally {
      setWorking(false)
    }
  }

  const handlePauseConfirm = async () => {
    setWorking(true)
    setError(null)
    try {
      await onPause(checkin.habit_def_id, {
        paused_until: pauseUntilIso,
        reason_codes: selectedReasons,
        source: 'habit_adjustment_checkin',
      })
      onDone()
      onOpenChange(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not pause this habit. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const summaryText = selectedAction
    ? summarize(selectedAction, chosenDetails, habitTitle)
    : 'This change only applies after you confirm it.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{copy.title || checkin.title || `Adjust ${habitTitle}`}</DialogTitle>
          <DialogDescription>
            {copy.body || checkin.body || 'A small change is enough here.'}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          {error && (
            <p className="text-xs text-error" role="alert">
              {error}
            </p>
          )}

          {step === 'diagnose' && (
            <div className="space-y-3">
              <p className="text-xs text-secondary">
                Pick anything that fits. There are no wrong answers — this just helps us suggest the
                gentlest next step.
              </p>
              {REASONS.map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-2 rounded-lg border border-soft px-3 py-2 text-sm text-primary"
                >
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(value)}
                    onChange={() => toggleReason(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
              <Button
                className="w-full"
                disabled={selectedReasons.length === 0 || working}
                onClick={() => void handleDiagnose()}
              >
                Continue
              </Button>
            </div>
          )}

          {step === 'options' && (
            <div className="space-y-3">
              {options.map((option, idx) => (
                <button
                  key={option.action_id}
                  type="button"
                  className={`w-full rounded-lg border px-3 py-3 text-left ${
                    selectedAction === option.action_id
                      ? 'border-brand bg-brand-subtle'
                      : 'border-soft bg-surface'
                  }`}
                  onClick={() => setSelectedAction(option.action_id)}
                >
                  <span className="text-sm font-medium text-primary">
                    {option.label}
                    {idx === 0 && (
                      <span className="ml-2 text-xs text-brand">Recommended</span>
                    )}
                  </span>
                </button>
              ))}

              {selectedAction === 'reduce_frequency' && (
                <div className="rounded-lg border border-soft px-3 py-3">
                  <p className="text-xs text-secondary mb-2">
                    Pick the days you want to keep (optional — we’ll choose for you if blank).
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAYS.map(([code, label]) => (
                      <label
                        key={code}
                        className={`flex items-center gap-1 rounded-full border px-2 py-1 text-xs cursor-pointer ${
                          preferredDays.includes(code)
                            ? 'border-brand bg-brand-subtle text-brand'
                            : 'border-soft text-secondary'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={preferredDays.includes(code)}
                          onChange={() => toggleDay(code)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {selectedAction === 'make_lighter' && (
                <div className="rounded-lg border border-soft px-3 py-3">
                  <label className="text-xs text-secondary" htmlFor="lighter-minutes">
                    Target minutes per session
                  </label>
                  <input
                    id="lighter-minutes"
                    type="number"
                    min={1}
                    max={240}
                    value={lighterMinutes}
                    onChange={e => setLighterMinutes(Number(e.target.value))}
                    className="mt-2 w-full rounded-lg border border-soft px-3 py-2 text-sm"
                  />
                </div>
              )}

              {(selectedAction === 'move_time' || selectedAction === 'move_window') && (
                <div className="rounded-lg border border-soft px-3 py-3 space-y-3">
                  <label className="block text-xs text-secondary" htmlFor="preferred-time-window">
                    Preferred window
                  </label>
                  <select
                    id="preferred-time-window"
                    value={preferredTimeWindow}
                    onChange={e => setPreferredTimeWindow(e.target.value)}
                    className="w-full rounded-lg border border-soft px-3 py-2 text-sm"
                  >
                    {TIME_WINDOWS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <label className="block text-xs text-secondary" htmlFor="preferred-start-time">
                    Approximate time
                  </label>
                  <input
                    id="preferred-start-time"
                    type="time"
                    value={preferredStartTime}
                    onChange={e => setPreferredStartTime(e.target.value)}
                    className="w-full rounded-lg border border-soft px-3 py-2 text-sm"
                  />
                </div>
              )}

              {selectedAction === 'swap_activity' && (
                <div className="rounded-lg border border-soft px-3 py-3">
                  <label className="text-xs text-secondary" htmlFor="swap-description">
                    What should this become?
                  </label>
                  <textarea
                    id="swap-description"
                    value={swapDescription}
                    onChange={e => setSwapDescription(e.target.value)}
                    placeholder="Example: brush teeth right after coffee, even if it is only a quick pass"
                    className="mt-2 min-h-20 w-full rounded-lg border border-soft px-3 py-2 text-sm"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={!canReviewSelectedAction()}
                  onClick={() => void handleReview()}
                >
                  Review change
                </Button>
                <Button
                  variant="outline"
                  onClick={handleEscalateToPause}
                  disabled={working}
                >
                  Pause instead
                </Button>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-3">
              <div className="rounded-lg border border-soft bg-surface p-3">
                <p className="text-sm font-medium text-primary">Here’s what will change</p>
                <p className="mt-1 text-xs text-secondary">{summaryText}</p>
              </div>
              <p className="text-xs text-secondary">
                This change only applies after you confirm it. Nothing has been saved yet.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button className="flex-1" disabled={working} onClick={() => void handleConfirm()}>
                  Confirm change
                </Button>
                <Button variant="outline" onClick={() => setStep('options')} disabled={working}>
                  Go back
                </Button>
                <Button variant="ghost" onClick={() => void handleKeepAsIs()} disabled={working}>
                  Keep as is
                </Button>
              </div>
            </div>
          )}

          {step === 'pause' && (
            <div className="space-y-3">
              <p className="text-sm text-secondary">
                Pause {habitTitle} and we’ll quietly bring it back when you’re ready.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {PAUSE_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm text-left ${
                      pauseChoice === preset.id
                        ? 'border-brand bg-brand-subtle text-brand'
                        : 'border-soft text-primary'
                    }`}
                    onClick={() => setPauseChoice(preset.id)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {pauseChoice === 'custom' && (
                <input
                  type="date"
                  className="w-full rounded-lg border border-soft px-3 py-2 text-sm"
                  value={customDate}
                  min={isoDateOnly(new Date(Date.now() + 86400000))}
                  onChange={e => setCustomDate(e.target.value)}
                />
              )}
              <p className="text-xs text-secondary">
                We’ll pause until <strong>{formatLongDate(pauseUntilIso)}</strong>.
              </p>
              <div className="flex gap-2">
                <Button className="flex-1" disabled={working} onClick={() => void handlePauseConfirm()}>
                  Pause until then
                </Button>
                {initialStep === 'diagnose' && (
                  <Button variant="outline" onClick={() => setStep('options')} disabled={working}>
                    Back to options
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
